import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import type {
  FeeFreeUntilStoreListQuery,
  AdminUpdateStoreFeeFreeUntilMutation,
  AdminUpdateStoreFeeFreeUntilMutationVariables,
} from "../../../__generated__/graphql";
import { notify, notifyError } from "../../../utils/toast";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

const FEE_FREE_UNTIL_STORE_LIST = gql(`
  query FeeFreeUntilStoreList {
    getStoreList {
      ok
      error
      result {
        id
        title
        address
        feeFreeUntil
      }
    }
  }
`);

const ADMIN_UPDATE_STORE_FEE_FREE_UNTIL = gql(`
  mutation AdminUpdateStoreFeeFreeUntil($storeId: Int!, $feeFreeUntil: DateTime) {
    adminUpdateStoreFeeFreeUntil(storeId: $storeId, feeFreeUntil: $feeFreeUntil) {
      ok
      error
    }
  }
`);

type StoreRow = NonNullable<
  FeeFreeUntilStoreListQuery["getStoreList"]
>["result"][number];

function formatFeeFreeUntil(
  value: string | Date | null | undefined
): string {
  if (value == null) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 선택한 날짜를 그날 00:00 UTC로 보내 서버에서 해당일 23:59:59 KST로 처리되도록 함 */
function toStartOfDayUTC(dateStr: string): string {
  return `${dateStr}T00:00:00.000Z`;
}

export default function AdminFeeFreeUntil() {
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [dateValue, setDateValue] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");

  useScrollToDetailPanelOnSelect(selectedStore);

  const { data, loading, error, refetch } = useQuery<FeeFreeUntilStoreListQuery>(
    FEE_FREE_UNTIL_STORE_LIST
  );
  const [adminUpdateStoreFeeFreeUntil, { loading: updating }] = useMutation<
    AdminUpdateStoreFeeFreeUntilMutation,
    AdminUpdateStoreFeeFreeUntilMutationVariables
  >(ADMIN_UPDATE_STORE_FEE_FREE_UNTIL, {
    onCompleted: (res) => {
      if (res.adminUpdateStoreFeeFreeUntil.ok) {
        notify("수수료 무료 기간이 반영되었습니다.");
        setDateValue("");
        refetch();
      } else {
        notifyError(
          res.adminUpdateStoreFeeFreeUntil.error ?? "설정에 실패했습니다."
        );
      }
    },
    onError: (e) => {
      notifyError(e.message);
    },
  });

  const fullList = data?.getStoreList?.ok ? data.getStoreList.result ?? [] : [];
  const keyword = searchKeyword.trim().toLowerCase();
  const list = keyword
    ? fullList.filter((store) =>
        store.title.toLowerCase().includes(keyword)
      )
    : fullList;

  const handleSetFeeFreeUntil = () => {
    if (!selectedStore || !dateValue.trim()) {
      notifyError("매장을 선택하고 날짜를 입력해주세요.");
      return;
    }
    adminUpdateStoreFeeFreeUntil({
      variables: {
        storeId: selectedStore.id,
        feeFreeUntil: toStartOfDayUTC(dateValue),
      },
    });
  };

  const handleClearFeeFreeUntil = () => {
    if (!selectedStore) {
      notifyError("매장을 선택해주세요.");
      return;
    }
    if (!window.confirm("해당 매장의 수수료 무료 기간을 해제하시겠습니까?")) {
      return;
    }
    adminUpdateStoreFeeFreeUntil({
      variables: {
        storeId: selectedStore.id,
        feeFreeUntil: null,
      },
    });
  };

  const handleSelectStore = (store: StoreRow) => {
    setSelectedStore(store);
    setDateValue(
      store.feeFreeUntil ? formatFeeFreeUntil(store.feeFreeUntil) : ""
    );
  };

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">에러: {error.message}</div>
      </div>
    );
  }
  if (!data?.getStoreList?.ok) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">
          {data?.getStoreList?.error ?? "목록을 불러올 수 없습니다."}
        </div>
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="수수료 무료 기간 설정"
      description="매장을 선택한 뒤 종료일을 설정하세요. 선택한 날짜를 포함하여 그날까지 수수료가 무료로 적용됩니다."
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명으로 검색",
        searchValue: searchKeyword,
        onSearchChange: setSearchKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={list.map((s) => ({
              id: s.id,
              title: s.title,
              address: s.address ?? undefined,
            }))}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (full) handleSelectStore(full);
            }}
            showImage={false}
            extraHeaders={
              <th className="px-4 py-2 font-medium text-gray-700">
                무료 종료일
              </th>
            }
            renderExtraCells={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (!full) return null;
              return (
                <td className="px-4 py-3 text-gray-600">
                  {formatFeeFreeUntil(full.feeFreeUntil ?? undefined)}
                </td>
              );
            }}
            renderExtraCard={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (!full) return null;
              return (
                <p className="text-xs text-gray-600 mt-1">
                  무료 종료일:{" "}
                  {formatFeeFreeUntil(full.feeFreeUntil ?? undefined)}
                </p>
              );
            }}
            emptyText={
              keyword
                ? "검색 결과가 없습니다."
                : "등록된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "수수료 무료 종료일",
        emptyText: "왼쪽 목록에서 매장을 선택하세요.",
        children: selectedStore ? (
          <>
            <div className="text-sm text-gray-700">
              <span className="font-medium">선택 매장:</span>{" "}
              {selectedStore.title} (ID: {selectedStore.id})
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                무료 종료일 (해당 날짜 포함)
              </label>
              <input
                type="date"
                value={dateValue}
                onChange={(e) => setDateValue(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                disabled={!dateValue.trim() || updating}
                onClick={handleSetFeeFreeUntil}
                className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? "저장 중..." : "설정"}
              </button>
              <button
                type="button"
                disabled={updating}
                onClick={handleClearFeeFreeUntil}
                className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                해제
              </button>
            </div>
          </>
        ) : null,
      }}
    />
  );
}
